import React, { useState, useEffect } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import Select from '@mui/material/Select';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import FileUpload from '../FileUpload';

type ContentType = 'sample-ballot' | 'candidate-list' | 'measure-list';
type SourceMode = 'pdf-url' | 'pdf-upload' | 'body-text';

interface AddPotentialAutoScouterContentProps {
  open: boolean;
  onClose: () => void;
  onSave: (contentType: ContentType, url: string, dataType?: 'pdf' | 'body-text', bodyText?: string) => Promise<void>;
  existingUrls: string[];
}

export const AddPotentialAutoScouterContent: React.FC<AddPotentialAutoScouterContentProps> = ({
  open,
  onClose,
  onSave,
  existingUrls
}) => {
  const [contentType, setContentType] = useState<ContentType>('sample-ballot');
  const [url, setUrl] = useState<string>('');
  const [sourceMode, setSourceMode] = useState<SourceMode>('pdf-url');
  const [bodyText, setBodyText] = useState<string>('');
  const [saving, setSaving] = useState<boolean>(false);
  const [uploading, setUploading] = useState<boolean>(false);
  const [uploadError, setUploadError] = useState<string>('');
  const [duplicateUrlError, setDuplicateUrlError] = useState<string>('');

  // Reset form when modal opens/closes
  useEffect(() => {
    if (open) {
      setContentType('sample-ballot');
      setUrl('');
      setSourceMode('pdf-url');
      setBodyText('');
      setUploading(false);
      setUploadError('');
      setDuplicateUrlError('');
    }
  }, [open]);

  // Check for duplicate URL
  const checkDuplicateUrl = (urlToCheck: string): boolean => {
    if (!urlToCheck.trim()) return false;
    const normalizedUrl = urlToCheck.trim().toLowerCase();
    return existingUrls.some(existing => existing.toLowerCase() === normalizedUrl);
  };

  // Handler for URL change with duplicate check
  const handleUrlChange = (newUrl: string) => {
    setUrl(newUrl);
    if (newUrl.trim() && checkDuplicateUrl(newUrl)) {
      setDuplicateUrlError('This URL already exists');
    } else {
      setDuplicateUrlError('');
    }
  };

  const handleContentTypeChange = (newContentType: ContentType) => {
    setContentType(newContentType);
    if (newContentType === 'sample-ballot' && sourceMode === 'body-text') {
      setSourceMode('pdf-url');
      setBodyText('');
    }
  };

  const handleSourceModeChange = (newSourceMode: SourceMode) => {
    setSourceMode(newSourceMode);
    setUrl('');
    setBodyText('');
    setUploadError('');
    setDuplicateUrlError('');
  };

  const handleUploadStart = () => {
    setUploading(true);
    setUrl('');
    setUploadError('');
    setDuplicateUrlError('');
  };

  const handleUploadSuccess = (uploadedUrl: string) => {
    setUploading(false);
    handleUrlChange(uploadedUrl);
  };

  const handleUploadError = (error: Error) => {
    setUploading(false);
    setUploadError(error?.message || 'Failed to upload PDF. Please try again.');
  };

  // Handler to save
  const handleSave = async () => {
    if (!url.trim()) {
      alert(sourceMode === 'pdf-upload' ? 'Please upload a PDF' : 'Please enter a URL');
      return;
    }

    // Check for duplicate URL
    if (checkDuplicateUrl(url)) {
      setDuplicateUrlError('This URL already exists');
      return;
    }

    if (sourceMode === 'body-text' && !bodyText.trim()) {
      alert('Please enter body text for body-text type');
      return;
    }

    setSaving(true);
    try {
      const dataType = sourceMode === 'body-text' ? 'body-text' : 'pdf';
      await onSave(contentType, url.trim(), dataType, sourceMode === 'body-text' ? bodyText.trim() : undefined);
      onClose();
    } catch (error) {
      console.error('Error saving content:', error);
      alert('Failed to add new content. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => !saving && !uploading && onClose()}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        Add Potential Content
      </DialogTitle>
      <DialogContent>
        <Box display="flex" flexDirection="column" gap={2} mt={1}>
          <FormControl fullWidth>
            <InputLabel>Content Type</InputLabel>
            <Select
              value={contentType}
              onChange={(e) => handleContentTypeChange(e.target.value as ContentType)}
              label="Content Type"
              disabled={saving || uploading}
            >
              <MenuItem value="sample-ballot">Sample Ballot</MenuItem>
              <MenuItem value="candidate-list">Candidate List</MenuItem>
              <MenuItem value="measure-list">Measure/Proposal List</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth>
            <InputLabel>Source</InputLabel>
            <Select
              value={sourceMode}
              onChange={(e) => handleSourceModeChange(e.target.value as SourceMode)}
              label="Source"
              disabled={saving || uploading}
            >
              <MenuItem value="pdf-url">PDF URL</MenuItem>
              <MenuItem value="pdf-upload">Upload PDF</MenuItem>
              {contentType !== 'sample-ballot' && (
                <MenuItem value="body-text">Body Text</MenuItem>
              )}
            </Select>
          </FormControl>

          {sourceMode === 'pdf-upload' ? (
            <>
              <Typography variant="body2">
                Drag and drop a PDF below, or choose a file to upload.
              </Typography>
              <FileUpload
                fileType="auto-scouter-content"
                onUploadStart={handleUploadStart}
                onUploadSuccess={handleUploadSuccess}
                onUploadError={handleUploadError}
              />
              {uploadError && (
                <Typography color="error" role="alert">
                  {uploadError}
                </Typography>
              )}
              {url && (
                <TextField
                  label="Uploaded PDF URL"
                  fullWidth
                  value={url}
                  InputProps={{ readOnly: true }}
                  error={!!duplicateUrlError}
                  helperText={duplicateUrlError || 'PDF uploaded successfully'}
                />
              )}
            </>
          ) : (
            <>
              <TextField
                label={sourceMode === 'body-text' ? 'Source URL' : 'PDF URL'}
                fullWidth
                value={url}
                onChange={(e) => handleUrlChange(e.target.value)}
                placeholder="https://example.com/ballot.pdf"
                disabled={saving}
                error={!!duplicateUrlError}
                helperText={duplicateUrlError}
              />
              {sourceMode === 'body-text' && (
                <TextField
                  label="Body Text"
                  fullWidth
                  multiline
                  rows={10}
                  value={bodyText}
                  onChange={(e) => setBodyText(e.target.value)}
                  placeholder="Paste the source content here..."
                  disabled={saving}
                />
              )}
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={onClose}
          disabled={saving || uploading}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
            disabled={saving || uploading || !url.trim() || !!duplicateUrlError || (sourceMode === 'body-text' && !bodyText.trim())}
          style={{
            backgroundColor: '#22034F',
            color: '#FFFFFF'
          }}
        >
          {saving ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

